import * as React from "react";
import styled from "@emotion/styled";
import sampleProfile from "assets/images/sample-profile_jackson-alves.jpg";
import {
  Alert,
  AllCaps,
  BetaBanner,
  Button,
  FlexRow,
  H2,
  H3,
  H4,
  IconArrow,
  IconRaisedHands,
  List,
  LoadingEllipses,
  Modal,
  Stack,
  Text,
  UnexpectedError,
} from "atoms";
import { GenericImage } from "features/images";
import { useGalleryConvert } from "../api/useGalleryConvert";

type Props = {
  artistId: string;
};

function BetaProfileConvertMessage({ artistId }: Props) {
  const { mutate, isLoading, isSuccess, isError, error } =
    useGalleryConvert(artistId);
  const { isOpen, open, close } = Modal.useModalState();

  const convert = () => mutate();

  React.useEffect(() => {
    if (isSuccess) {
      close();
    }
  }, [isSuccess, close]);

  return (
    <>
      <BetaBanner>
        <FlexRow itemsFlex="1 1 auto" flexWrap="nowrap" alignItems="center">
          <SIconRaisedHands />
          <FlexRow justifyContent="space-between">
            <Stack gap="xs">
              <H2 size="sm">
                You&apos;re officially invited to join our beta for new artist
                profiles!
              </H2>
              <Text size="xs">
                Join a select group of artists in enjoying upgraded
                functionality to showcase your work in new and exciting ways.
              </Text>
            </Stack>
            {!isSuccess && (
              <Button size="xs" onClick={open}>
                {isLoading ? (
                  <LoadingEllipses text="Converting Profile" />
                ) : (
                  "Join Beta"
                )}
              </Button>
            )}
          </FlexRow>
        </FlexRow>
      </BetaBanner>

      <SModal isOpen={isOpen} onClose={close}>
        <TwoCols>
          <SampleProfile
            src={sampleProfile}
            sizes="100vw, (min-width: 831px) 50vw, (min-width: 1440px) 600px"
            alt="A screenshot of an artist profile using the new artist profile template"
          />
          <Stack gap="sm">
            <H2 size="lg">Welcome to a new home for&nbsp;your&nbsp;art</H2>
            <Text size="xs">
              Our new Artist Profiles are a simple yet powerful way for you to
              showcase, categorize, sell, and land opportunities for your art.
            </Text>
            <Stack gap="xs">
              <H3 as="span" size="xs" font="body" textTransform="none">
                <AllCaps>Hug</AllCaps>&apos;s New Tab Organization
              </H3>
              <SList>
                <Text as="li" size="xs">
                  <H4 as="span" size="xs" font="body" textTransform="none">
                    Featured:
                  </H4>{" "}
                  Your new Profile homepage, highlighting your favorite works,
                  latest updates and reviews.
                </Text>
                <Text as="li" size="xs">
                  <H4 as="span" size="xs" font="body" textTransform="none">
                    Portfolio:
                  </H4>{" "}
                  Enjoy upgraded functionality like galleries and video uploads
                  to house your entire body of work.{" "}
                </Text>
                <Text as="li" size="xs">
                  <H4 as="span" size="xs" font="body" textTransform="none">
                    Updates:
                  </H4>{" "}
                  A place for you to share your latest and most important news
                  with your followers.
                </Text>
                <Text as="li" size="xs">
                  <H4 as="span" size="xs" font="body" textTransform="none">
                    Reviews:
                  </H4>{" "}
                  Where our community shares their love for both you and your
                  art.
                </Text>
                <Text as="li" size="xs">
                  <H4 as="span" size="xs" font="body" textTransform="none">
                    About:
                  </H4>{" "}
                  Tell your story more effectively than ever with both text and
                  video
                </Text>
              </SList>
            </Stack>
            {!isSuccess && (
              <div style={{ padding: "1em 0" }}>
                <Button onClick={convert} disabled={isLoading}>
                  {isLoading ? (
                    <LoadingEllipses text="Converting Profile" />
                  ) : (
                    <span>Convert My Profile Now</span>
                  )}
                  {!isLoading && <IconArrow dir="r" />}
                </Button>
                {isError && <UnexpectedError error={error} />}
              </div>
            )}
          </Stack>
        </TwoCols>
        <Alert type="error">
          <Stack gap="xxs">
            <Text size="xs" textAlign="center">
              🚨 <strong>Note:</strong> Once you join the beta, you{" "}
              <em>will not</em> be able to revert to the old Profile.
            </Text>
          </Stack>
        </Alert>
      </SModal>
    </>
  );
}

const SModal = styled(Modal)(({ theme }) => ({
  overflow: "auto",
  [theme.breakpoints.desktop]: {
    height: "auto",
    maxHeight: "90vh",
    maxWidth: 1200,
  },
}));

const TwoCols = styled.div(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    display: "flex",
    gap: "2rem",
    alignItems: "center",
  },
}));
const SList = styled(List)(({ theme }) => ({
  "&&": { fontSize: theme.fontSizes.xs },
}));
const SampleProfile = styled(GenericImage)(({ theme }) => ({
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  marginBottom: "1em",
  boxShadow: theme.boxShadow.light,
}));
const SIconRaisedHands = styled(IconRaisedHands)(({ theme }) => ({
  display: "none",
  margin: "0 auto",
  color: theme.colors.fg,
  ".accent-1": {
    color: theme.colors.accent3,
  },
  ".accent-2": {
    color: theme.colors.accent1,
  },
  ".accent-3": {
    color: theme.colors.accent2,
  },
  [theme.breakpoints.xs]: {
    display: "block",
    width: 40,
    "&&": {
      flex: "0 0 40px",
    },
  },
  [theme.breakpoints.tablet]: {
    "&&": { flex: "0 0 60px" },
    width: 60,
  },
}));
export { BetaProfileConvertMessage };
